import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, SendHorizonal, MessageCircle } from "lucide-react";
import { useLanguage } from "../i18n/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WA_FLOAT = "https://wa.me/918922004004?text=Hello%20Uma%20Enterprises,%20I%20want%20to%20enquire%20about%20Solar/Wind/AI%20solutions.";

export const FloatingWidgets = () => {
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);
  const sessionRef = useRef(null);

  if (!sessionRef.current) {
    sessionRef.current = localStorage.getItem("uma_chat_session") || `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("uma_chat_session", sessionRef.current);
  }

  useEffect(() => {
    setMessages((m) => (m.length === 0 ? [{ role: "assistant", text: t.chat.greeting }] : m));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (data === "[DONE]") continue;
          try {
            const { delta } = JSON.parse(data);
            if (delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + delta };
                return copy;
              });
            }
          } catch { /* partial chunk */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Connection issue — please WhatsApp us on 8922004004." };
        return copy;
      });
    }
    setStreaming(false);
  };

  return (
    <>
      <a
        href={WA_FLOAT}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="floating-whatsapp-widget"
        aria-label="Chat on WhatsApp"
        className="fixed bottom-6 left-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] shadow-[0_8px_32px_rgba(37,211,102,0.45)] transition-transform duration-300 hover:scale-110"
      >
        <span className="absolute inset-0 rounded-full bg-[#25D366] animate-pulse-ring" />
        <MessageCircle size={26} className="relative text-white" fill="white" />
      </a>

      <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3">
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.95 }}
              transition={{ duration: 0.25 }}
              className="glass-card flex h-[460px] w-[340px] flex-col overflow-hidden rounded-3xl shadow-2xl sm:w-[380px]"
              data-testid="gemini-chatbot-panel"
            >
              <div className="flex items-center justify-between border-b border-white/10 bg-navy-900/80 px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-xl bg-white">
                    <img src="/uma-logo-icon.png" alt="Uma AI Assistant" className="h-7 w-7 object-contain" data-testid="gemini-chatbot-avatar" />
                  </div>
                  <div>
                    <div className="font-display text-sm font-bold text-white">{t.chat.title}</div>
                    <div className="text-[10px] text-slate-400">{t.chat.subtitle}</div>
                  </div>
                </div>
                <button onClick={() => setOpen(false)} className="text-slate-400 transition-colors hover:text-white" aria-label="Close chat" data-testid="gemini-chatbot-close-btn">
                  <X size={18} />
                </button>
              </div>

              <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="gemini-chatbot-messages">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                        m.role === "user"
                          ? "rounded-br-sm bg-solar text-amber-950 font-medium"
                          : "rounded-bl-sm border border-white/10 bg-navy-950/80 text-slate-200"
                      }`}
                    >
                      {m.text || (streaming && i === messages.length - 1 ? (
                        <span className="inline-flex items-center gap-1 text-slate-500">
                          {t.chat.typing}
                          <span className="animate-pulse">…</span>
                        </span>
                      ) : "")}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              <div className="flex items-center gap-2 border-t border-white/10 bg-navy-900/80 p-3">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={t.chat.placeholder}
                  data-testid="gemini-chatbot-input"
                  className="flex-1 rounded-full border border-white/10 bg-navy-950/70 px-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none transition-colors focus:border-solar/60"
                />
                <button
                  onClick={send}
                  disabled={streaming}
                  data-testid="gemini-chatbot-send-btn"
                  aria-label={t.chat.send}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-solar text-amber-950 transition-colors hover:bg-solar-yellow disabled:opacity-50"
                >
                  <SendHorizonal size={17} />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          onClick={() => setOpen(!open)}
          data-testid="gemini-chatbot-trigger"
          className="flex items-center gap-2 rounded-full bg-solar px-5 py-3.5 text-sm font-bold text-amber-950 shadow-[0_8px_32px_rgba(245,158,11,0.4)] transition-transform duration-300 hover:scale-105"
        >
          <Sparkles size={18} />
          <span className="hidden sm:inline">{t.chat.title}</span>
        </button>
      </div>
    </>
  );
};
